import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  colors,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AssessmentQuestion,
  AssessmentQuestionType,
  FileUploadQuestion,
  MultipleChoiceQuestion,
  TextInputQuestion,
} from '../../utils/assessmentsQuestions';
import { useEffect, useState } from 'react';
import Question from '../../components/Assessment/question';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Http } from '@mui/icons-material';
import HttpHelper from '../../utils/HttpHelper';
import FileUpload from '../../components/common/FileUpload';
import useFileHandlers from '../../hooks/useFileHandlers';

interface MultipleChoice {
  id: number;
  option: string;
  isCorrect: boolean;
}

interface Question {
  id: number;
  text: string;
  type: 0 | 1 | 2 | 3;
  file?: string;
  maxGrade: number;
  possibleChoice?: MultipleChoice[];
}

const EditAssessment: React.FC = () => {
  const navigate = useNavigate();

  const { assessmentId } = useParams<{ assessmentId: string }>();
  const { handleFileUpload, handleDeleteFile, modalOpen, setModalOpen } =
    useFileHandlers();

  const GET_ASSESSMENT_QUESTIONS_ENDPOINT: string = `assessments/${assessmentId}/questions`;
  const UPDATE_ASSESSMENT_ENDPOINT: string = `assessments/${assessmentId}/update`;

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isOptional, setIsOptional] = useState<boolean>(false);
  const [assessmentLoaded, setAssessmentLoaded] = useState<boolean>(false);
  const [questions, setQuestions] = useState<AssessmentQuestion[]>([]);
  const [descriptionFieldEmpty, setDescriptionFieldEmpty] =
    useState<boolean>(false);
  const [choiceFieldsEmpty, setChoiceFieldsEmpty] = useState<boolean>(false);
  const [fileDeleted, setFileDeleted] = useState<boolean[]>(
    questions.map(() => false),
  );

  const getAssessment = () => {
    HttpHelper.get(`assessments/${assessmentId}`).then(assessmentResponse => {
      setTitle(assessmentResponse.title);
      setDescription(assessmentResponse.description);
      setIsOptional(!!assessmentResponse.isOptional);
      setAssessmentLoaded(true);
    });
    HttpHelper.get(GET_ASSESSMENT_QUESTIONS_ENDPOINT).then(response => {
      let newQuestions = [];

      console.log(`RESPONSE: ${JSON.stringify(response)}`);

      for (let i = 0; i < response.length; i++) {
        let questionTypes = Object.keys(AssessmentQuestionType);

        console.log(`QUESTION TYPES: ${JSON.stringify(questionTypes)}`);

        switch (response[i].type) {
          case AssessmentQuestionType.TextInput:
            newQuestions.push({
              id: response[i].id,
              type: AssessmentQuestionType.TextInput,
              description: response[i].text,
              points: response[i].maxGrade,
              answer: '',
              file: response[i].file,
            } as TextInputQuestion);
            break;

          case AssessmentQuestionType.FileUpload:
            newQuestions.push({
              id: response[i].id,
              type: AssessmentQuestionType.FileUpload,
              description: response[i].text,
              points: response[i].maxGrade,
              answer: '',
              file: response[i].file,
            } as FileUploadQuestion);
            break;

          case AssessmentQuestionType.MultipleChoice:
          case AssessmentQuestionType.SingleChoice:
            let choices = [];

            for (let j = 0; j < response[i].possibleChoice.length; j++) {
              choices.push({
                choice_id: response[i].possibleChoice[j].id,
                text: response[i].possibleChoice[j].option,
                isCorrect: response[i].possibleChoice[j].isCorrect,
              });
            }

            // Sort choices by choice_id to preserve order
            choices.sort((a, b) => a.choice_id - b.choice_id);

            newQuestions.push({
              id: response[i].id,
              type: response[i].type,
              description: response[i].text,
              points: response[i].maxGrade,
              choices: choices,
              answer: [],
              file: response[i].file,
            } as MultipleChoiceQuestion);
            break;
        }
      }

      setQuestions(newQuestions);
    });
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDescription(event.target.value);
  };

  useEffect(() => {
    getAssessment();
  }, []);

  const handleQuestionTypeChange = (
    index: number,
    type: AssessmentQuestionType,
  ) => {
    const updatedQuestions = questions.map((q, idx) => {
      if (idx === index) {
        switch (type) {
          case AssessmentQuestionType.TextInput:
            return {
              ...q,
              type: type,
              description: q.description,
              answer: typeof q.answer === 'string' ? q.answer : '',
              points: q.points,
            } as TextInputQuestion;

          case AssessmentQuestionType.FileUpload:
            return {
              ...q,
              type: type,
              description: q.description,
              answer: typeof q.answer === 'string' ? q.answer : '',
              points: q.points,
            } as FileUploadQuestion;

          case AssessmentQuestionType.MultipleChoice:
            return {
              ...q,
              type: type,
              choices:
                q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice
                  ? q.choices
                  : [{ choice_id: Date.now(), text: '' }],
              answer:
                q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice
                  ? q.answer
                  : [],
              points: q.points,
            } as MultipleChoiceQuestion;

          case AssessmentQuestionType.SingleChoice:
            return {
              ...q,
              type: type,
              choices:
                q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice
                  ? q.choices
                  : [{ choice_id: Date.now(), text: '' }],
              answer:
                q.type === AssessmentQuestionType.MultipleChoice || q.type === AssessmentQuestionType.SingleChoice
                  ? q.answer
                  : [],
              points: q.points,
            };
        }
      }
      return q;
    });
    setQuestions(updatedQuestions);
  };

  const handleQuestionChange = (
    index: number,
    updatedQuestion: AssessmentQuestion,
  ) => {
    const updatedQuestions = questions.map((q, idx) =>
      idx === index ? updatedQuestion : q,
    );
    setQuestions(updatedQuestions);
  };

  const handleAddQuestion = () => {
    setQuestions(prevQuestions => [
      ...prevQuestions,
      {
        id: null,
        type: AssessmentQuestionType.TextInput,
        description: '',
        answer: '',
        points: 0,
      },
    ]);
  };

  // const handleDeleteQuestion = (index: number, questionId: number | null) => {
  //     const updatedQuestions = questions.filter((q, idx) => idx !== index);
  //     setQuestions(updatedQuestions);

  //     HttpHelper.delete(`assessments/${assessmentId}/question/${questionId}/delete`);
  // }
  const handleDeleteQuestion = async (
    index: number,
    questionId: number | null,
  ) => {
    // remove locally first
    const updatedQuestions = questions.filter((q, idx) => idx !== index);
    setQuestions(updatedQuestions);

    // only call backend if this question was persisted (has an id)
    if (questionId != null) {
      try {
        await HttpHelper.delete(
          `assessments/${assessmentId}/question/${questionId}/delete`,
        );
      } catch (err) {
        console.error('Failed to delete question', err);
        // optional: show notification or revert local state on failure
      }
    }
  };

  const handleSaveAssessment = async (event: React.FormEvent) => {
    event.preventDefault();

    const assessmentData = {
      assessment: {
        title: title,
        description: description,
        isOptional: isOptional,
      },
    };

    const oldQuestions = await HttpHelper.get(
      `assessments/${assessmentId}/questions`,
    );
    const oldQuestionIds = oldQuestions.map(
      (question: { id: number }) => question.id,
    );

    // Prevent accidental overwrite before initial load
    if (!assessmentLoaded) {
      return;
    }

    // Ensure assessment info is updated first and wait for completion
    await HttpHelper.put(UPDATE_ASSESSMENT_ENDPOINT, assessmentData);

    // Create/update questions sequentially and include an explicit order index so
    // the backend can persist the intended ordering.
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      let questionData = {
        question: {
          text: question.description,
          type: question.type,
          maxGrade: question.points,
          file: question.file,
          order: i,
        },
        possibleChoices: [] as { option: string; isCorrect: number }[],
      };

      if (question.type === AssessmentQuestionType.MultipleChoice || question.type === AssessmentQuestionType.SingleChoice) {
        question.choices.forEach(choice => {
          questionData.possibleChoices.push({
            option: choice.text,
            isCorrect: choice.isCorrect ? 1 : 0,
          });
        });
      }

      try {
        if (question.id != null && oldQuestionIds.includes(question.id)) {
          await HttpHelper.put(
            `assessments/${assessmentId}/question/${question.id}/update`,
            questionData,
          );
        } else {
          await HttpHelper.post(
            `assessments/${assessmentId}/question/create`,
            questionData,
          );
        }
      } catch (err) {
        console.error('Failed to save question', err);
        // continue processing the rest but consider surfacing an error to the user
      }
    }

    navigate('/manage-assessments');
  };

  const onFileSelect = (index: number, file: File) => {
    handleFileUpload(index, file, assessmentId!, questions, setQuestions);
  };

  const onFileDelete = (index: number) => {
    handleDeleteFile(index, assessmentId!, questions, setQuestions);
  };

  return (
    <Box
      sx={{
        width: '95%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '96px',
        display: 'flex',
        justifySelf: 'center',
      }}
    >
      <Grid container>
        <Grid item xs={3} />
        <Grid item xs={6}>
          <Typography
            align="center"
            variant="h4"
            sx={{
              fontWeight: 'bold',
              marginBottom: '24px',
            }}
          >
            Edit Assessment
          </Typography>
          <Stack
            direction="column"
            spacing={4}
            mb={2}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Card>
              <CardContent
                sx={{
                  marginTop: '24px',
                  marginBottom: '24px',
                  marginLeft: '48px',
                  marginRight: '48px',
                }}
              >
                <Typography
                  align="left"
                  variant="h4"
                  gutterBottom
                  sx={{
                    width: '80%',
                    fontWeight: 'bold',
                    marginBottom: '32px',
                  }}
                >
                  Assessment Info
                </Typography>
                <TextField
                  fullWidth
                  label="Assessment Title"
                  variant="outlined"
                  value={title}
                  onChange={handleTitleChange}
                />
                <Divider sx={{ my: 3 }} />
                <TextField
                  fullWidth
                  label="Assessment Description"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={description}
                  onChange={handleDescriptionChange}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isOptional}
                      onChange={e => setIsOptional(e.target.checked)}
                    />
                  }
                  label="Optional assessment"
                />
              </CardContent>
            </Card>
            {questions.map((question, index) => {
              return (
                <Card key={index}>
                  <CardContent
                    sx={{
                      marginTop: '24px',
                      marginBottom: '24px',
                      marginLeft: '48px',
                      marginRight: '0px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          marginRight: '16px',
                        }}
                      >
                        <Typography
                          align="left"
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                          }}
                        >
                          Question {index + 1}
                        </Typography>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          sx={{
                            marginTop: '1rem',
                          }}
                          required
                        >
                          <InputLabel>Question Type</InputLabel>
                          <Select
                            value={question.type}
                            required
                            onChange={e =>
                              handleQuestionTypeChange(
                                index,
                                e.target.value as AssessmentQuestionType,
                              )
                            }
                            label="Question Type"
                          >
                            <MenuItem
                              value={AssessmentQuestionType.MultipleChoice}
                            >
                              Multiple Choice
                            </MenuItem>
                            <MenuItem
                              value={AssessmentQuestionType.SingleChoice}
                            >
                              Single Choice
                            </MenuItem>
                            <MenuItem value={AssessmentQuestionType.TextInput}>
                              Text Input
                            </MenuItem>
                            <MenuItem value={AssessmentQuestionType.FileUpload}>
                              File Upload
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <Question
                          question={question}
                          onChange={updatedQuestion =>
                            handleQuestionChange(index, updatedQuestion)
                          }
                          descriptionFieldEmpty={descriptionFieldEmpty}
                          choiceFieldsEmpty={choiceFieldsEmpty}
                        />
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          <FileUpload
                            fileUploadHandler={file =>
                              file && onFileSelect(index, file)
                            }
                            buttonText="Upload File"
                            deleted={fileDeleted[index]}
                          />
                          <></>
                          <Button
                            onClick={() => onFileDelete(index)}
                            sx={{
                              color: 'red',
                              textTransform: 'uppercase',
                              ml: 2,
                            }}
                          >
                            Delete File
                          </Button>
                        </Box>
                        {typeof question.file === 'string' && question.file && (
                          <Typography
                            variant="body1"
                            align="left"
                            sx={{ ml: 2, mt: 2 }}
                          >
                            {question.file}
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="large"
                        onClick={() =>
                          handleDeleteQuestion(index, question.id ?? null)
                        }
                        color="inherit"
                      >
                        <DeleteIcon sx={{ color: 'red' }} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem',
            }}
          >
            <IconButton
              onClick={handleAddQuestion}
              sx={{ color: 'var(--ttg-black)' }}
            >
              <AddCircleIcon />
            </IconButton>
          </Box>
          <form onSubmit={handleSaveAssessment}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: 'var(--ttg-green)',
                '&:hover': {
                  backgroundColor: 'var(--button-hover-green)',
                },
                marginTop: 3,
                marginBottom: 3,
              }}
              disabled={!assessmentLoaded}
            >
              Save Changes
            </Button>
          </form>
        </Grid>
        <Grid item xs={3} />
      </Grid>
    </Box>
  );
};

export default EditAssessment;
